import { Side, useTooltip } from "@soft-db/ui";
import {
	defineComponent,
	getCurrentInstance,
	nextTick,
	onMounted,
	onUnmounted,
	PropType,
	reactive,
	ref,
	watch,
} from "vue";

export default defineComponent({
	name: "AppTextTooltip",
	inheritAttrs: false,
	props: {
		as: [String, Object],
		value: String,
		side: String as PropType<Side>,
	},
	setup(props, { attrs }) {
		const instance = getCurrentInstance();
		if (!instance) throw new Error("Vue not found");
		const rootEl = ref<unknown>();

		const overflow = ref(false);

		function updateOverflow() {
			const root = rootEl.value;

			if (root == null) {
				return;
			}

			let e: HTMLElement | undefined;
			if (root instanceof HTMLElement) {
				e = root;
			} else if (typeof root === "object" && "$el" in root && root.$el instanceof HTMLElement) {
				e = root.$el;
			}

			if (e) {
				overflow.value = e.offsetWidth < e.scrollWidth;
			}
		}

		onMounted(() => {
			nextTick(() => updateOverflow());
			window.addEventListener("resize", updateOverflow);
		});
		onUnmounted(() => {
			window.removeEventListener("resize", updateOverflow);
		});

		const tooltipProps = reactive({
			tooltip: "",
			hideOverflow: true,
			side: props.side || ("bottom" as const),
		});

		watch(
			[() => props.value, overflow],
			() => {
				if (!overflow.value) tooltipProps.tooltip = "";
				else tooltipProps.tooltip = props.value || "";
			},
			{ immediate: true }
		);

		const { renderWithTooltip } = useTooltip(tooltipProps);

		return () => {
			const Tag = (props.as ?? "div") as any;

			return renderWithTooltip(
				<Tag ref={rootEl} class="truncate" {...attrs}>
					{props.value}
				</Tag>
			);
		};
	},
});
